import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const METHODS = ["WhatsApp", "Phone Call", "Visit", "Other"];

export function LogContactDialog({ open, onOpenChange, entity, stages, onSaved }) {
  const [form, setForm] = useState({ method: "WhatsApp", notes: "", status: "", frequency_days: "7", next_contact: "", next_time: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm({ method: "WhatsApp", notes: "", status: "", frequency_days: "7", next_contact: "", next_time: "" }); }, [entity, open]);

  if (!entity) return null;
  const isLead = entity.type === "lead";

  const save = async () => {
    setSaving(true);
    const body = { method: form.method, notes: form.notes, frequency_days: Number(form.frequency_days) };
    if (form.status) body.status = form.status;
    if (form.next_contact) body.next_contact = form.next_contact;
    if (form.next_time) body.next_time = form.next_time;
    const url = isLead ? `/crm/leads/${entity.id}/log-contact` : `/crm/customers/${entity.id}/log-contact`;
    try {
      const { data } = await api.post(url, body);
      toast.success(`Contact logged · next follow-up ${data.next_contact}`);
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to log contact"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="log-contact-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Log {isLead ? "Lead" : ""} Contact</DialogTitle>
          <p className="text-sm text-muted-foreground">{entity.name}{entity.ref ? ` · ${entity.ref}` : ""}</p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Contact Method Used</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="lc-method" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{isLead ? "Move Stage To" : "Move Status To"}</Label>
              <Select value={form.status || "__keep"} onValueChange={(v) => setForm({ ...form, status: v === "__keep" ? "" : v })}>
                <SelectTrigger data-testid="lc-status" className="mt-1"><SelectValue placeholder="Keep current" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__keep">Keep current</SelectItem>
                  {(stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Follow-Up Notes</Label><Textarea data-testid="lc-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Frequency</Label>
              <Select value={String(form.frequency_days)} onValueChange={(v) => setForm({ ...form, frequency_days: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{[3,7,14,30].map((d) => <SelectItem key={d} value={String(d)}>Every {d} days</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Next Contact Date</Label><Input data-testid="lc-next-date" type="date" value={form.next_contact} onChange={(e) => setForm({ ...form, next_contact: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Next Time</Label><Input type="time" value={form.next_time} onChange={(e) => setForm({ ...form, next_time: e.target.value })} className="mt-1" /></div>
          </div>
          <p className="text-xs text-muted-foreground">Saving sets Last Contact to today. Next follow-up uses the date above if set, otherwise the frequency.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="lc-save" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const BUCKET_BADGE = {
  overdue: "bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]",
  due_today: "bg-[#FBF1DF] text-[#7A4B12] border border-[#E7D0A8]",
  upcoming: "bg-[#EAF1F7] text-[#1E3E5C] border border-[#C7D7E6]",
};
export const BUCKET_LABEL = { overdue: "Overdue", due_today: "Due Today", upcoming: "Upcoming" };
